/**
 * The two routes a tasks tab needs for the rail to open over it.
 *
 * A task is read and written in one panel — the rail — on the tasks list, on a
 * project's board and on its plan. Everywhere else the same `<app-tasks>` table
 * had three different surfaces for one object: the eye left the record for a
 * full page, the pencil and « Ajouter » opened a modal whose fields were
 * hand-written, so the type's layout and the organization's custom fields were
 * invisible there. One object, three answers, and only one of them complete.
 *
 * Declared through a factory rather than copied into each module: there are six
 * hosts today — a contact and every contact type, an opportunity, a product, a
 * piece of equipment, a work order's checklist and its phases — and a seventh
 * mounting the tasks tab would otherwise have to know this file exists. The
 * names are a convention `Tasks.vue` resolves on its own, so wiring a new host
 * is this one line.
 *
 * ```js
 * { path: 'tasks', name: 'contact-tasks', component: TabTasks, props: true,
 *   children: taskRailRoutes('contact-tasks') }
 * ```
 *
 * A **named** view, like the tasks list's own: a host that shows a record beside
 * its rows already has a default `<router-view>`, and the panel declared as that
 * one would be drawn twice.
 */
export function taskRailRoutes(baseName) {
    const panel = () => import('../components/tasks/TaskPanel.vue');

    return [
        {
            // `task/` rather than `new`: these sit under a tab whose own path
            // segment is already taken, and a bare `:id` would swallow any
            // sibling the tab adds later.
            path: 'task/new',
            name: `${baseName}-task-new`,
            components: { slideover: panel },
            // The owner is not in this path — the tab already knows what record
            // it hangs off, and hands it to the panel through the slide-over.
            props: { slideover: { create: true } }
        },
        {
            path: 'task/:taskId(\\d+)',
            name: `${baseName}-task-detail`,
            components: { slideover: panel },
            props: { slideover: true }
        }
    ];
}

/** The suffixes above, for a host that has to tell its own route from the rail's. */
export const TASK_RAIL_SUFFIXES = ['-task-new', '-task-detail'];
